import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The medium window's rail overlay (docs/platform-standards.md §18, KPR-1761).
 *
 * In a medium window the rail is drawn folded (56px) and opens over the page on
 * demand: a tap on the wordmark, or ⌘\ (Ctrl+\). The open rail is a sheet. It
 * closes on a pick, on Esc and on a tap outside (the scrim); it holds Tab while
 * open; and focus returns to the control that opened it.
 *
 *   const { overlayOpen, openRail, closeRail, railRef, brandButtonRef, onRailClick } =
 *     useRailOverlay({ foldedRail, disabled, pathname, onToggleFold });
 *
 * `foldedRail` is "this window draws the rail folded" (medium, not short);
 * `onToggleFold` is what ⌘\ does everywhere else (fold or unfold the rail, the
 * person's own choice); `disabled` is the phone chrome, which has no rail.
 */
export default function useRailOverlay({ foldedRail, disabled, pathname, onToggleFold }) {
	const [wanted, setWanted] = useState(false);
	const overlayOpen = wanted && foldedRail;
	const openerRef = useRef(null);
	const brandButtonRef = useRef(null);
	const railRef = useRef(null);

	const openRail = useCallback(() => {
		openerRef.current = document.activeElement;
		setWanted(true);
	}, []);

	// `restoreFocus` is false for a pick that opens something else (the
	// palette, the inbox), which takes focus itself.
	const closeRail = useCallback((restoreFocus = true) => {
		setWanted(false);
		if (!restoreFocus) { openerRef.current = null; return; }
		// After the folded rail has drawn again: the opener may be the brand
		// button, which the open rail replaced with a link to Home.
		window.setTimeout(() => {
			const opener = openerRef.current;
			openerRef.current = null;
			const target = opener && opener.isConnected && opener !== document.body ? opener : brandButtonRef.current;
			if (target && typeof target.focus === 'function') target.focus();
		}, 0);
	}, []);

	// ⌘\ (Ctrl+\): in a medium window it opens or closes the overlay, in an
	// expanded one it folds or unfolds the rail as the chevron does.
	useEffect(() => {
		if (disabled) return undefined;
		const handler = (event) => {
			if (!((event.metaKey || event.ctrlKey) && event.key === '\\')) return;
			event.preventDefault();
			if (!foldedRail) onToggleFold();
			else if (overlayOpen) closeRail();
			else openRail();
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [disabled, foldedRail, overlayOpen, openRail, closeRail, onToggleFold]);

	// A pick closes the overlay: navigating anywhere does. Leaving medium (a
	// resize, a rotation) puts it away for good.
	useEffect(() => { setWanted(false); }, [pathname]);
	useEffect(() => { if (!foldedRail) setWanted(false); }, [foldedRail]);

	// The open overlay is a sheet: Esc closes it, Tab stays inside it, and
	// focus moves in when it opens.
	useEffect(() => {
		if (!overlayOpen) return undefined;
		const rail = railRef.current;
		const stopsOf = () => [...rail.querySelectorAll('a[href], button:not([disabled])')].filter(node => !node.hidden);
		const aboveOpen = () => document.querySelector('.ant-dropdown:not(.ant-dropdown-hidden), .ant-modal-wrap, .ant-drawer-open');
		if (rail) {
			const [first] = stopsOf();
			if (first) first.focus();
		}
		const onKey = (event) => {
			if (event.key === 'Escape') {
				// A menu or modal open above the rail takes Esc first.
				if (aboveOpen()) return;
				event.preventDefault();
				closeRail();
				return;
			}
			if (event.key !== 'Tab' || !rail || aboveOpen()) return;
			const stops = stopsOf();
			if (!stops.length) return;
			const inside = rail.contains(document.activeElement);
			if (event.shiftKey && (!inside || document.activeElement === stops[0])) {
				event.preventDefault();
				stops[stops.length - 1].focus();
			} else if (!event.shiftKey && (!inside || document.activeElement === stops[stops.length - 1])) {
				event.preventDefault();
				stops[0].focus();
			}
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [overlayOpen, closeRail]);

	// A pick inside the overlay: a link closes it; the search and the bell
	// open something that takes focus, so focus is not sent back. Menus the
	// rail opens render in a portal, whose clicks bubble here in React.
	const onRailClick = (event) => {
		if (!overlayOpen || !event.currentTarget.contains(event.target)) return;
		if (event.target.closest('a[href]')) { closeRail(); return; }
		if (event.target.closest('.rail__search, .rail__nav-row--button')) closeRail(false);
	};

	return { overlayOpen, openRail, closeRail, railRef, brandButtonRef, onRailClick };
}
